/**
 * ThreeDView.jsx — Three.js orbit preview panel
 *
 * Loads Three.js r128 from CDN (already allowed), builds the scene from
 * the DCEL state via threeDBuilder, and renders an interactive orbit view.
 *
 * Falls back gracefully if WebGL is not available.
 * No npm packages needed — Three.js is loaded via a <script> tag on first mount.
 */
import React, { useRef, useEffect, useState, useCallback } from "react";
import { buildScene, getBoundingBox } from "../engine/threeDBuilder";

const THREE_CDN = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";

// Load Three.js from CDN once — returns a promise
let threePromise = null;
function loadThree() {
  if (window.THREE) return Promise.resolve(window.THREE);
  if (threePromise) return threePromise;
  threePromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = THREE_CDN;
    s.onload  = () => resolve(window.THREE);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return threePromise;
}

// Simple OrbitControls (mouse-only, no import needed)
function makeOrbitControls(camera, domElement) {
  let isDragging = false, isRight = false;
  let lastX = 0, lastY = 0;
  let theta = 0.6, phi = 1.0, radius = 10;
  let targetX = 0, targetY = 0, targetZ = 0;

  function update() {
    const sinPhi = Math.sin(phi), cosPhi = Math.cos(phi);
    const sinTheta = Math.sin(theta), cosTheta = Math.cos(theta);
    camera.position.set(
      targetX + radius * sinPhi * cosTheta,
      targetY + radius * cosPhi,
      targetZ + radius * sinPhi * sinTheta
    );
    camera.lookAt(targetX, targetY, targetZ);
  }

  function onDown(e) {
    isDragging = true;
    isRight = e.button === 2;
    lastX = e.clientX; lastY = e.clientY;
  }
  function onMove(e) {
    if (!isDragging) return;
    const dx = e.clientX - lastX, dy = e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    if (isRight) {
      // Pan
      const panSpeed = radius * 0.001;
      const right = { x: Math.cos(theta), z: Math.sin(theta) };
      targetX -= right.x * dx * panSpeed;
      targetZ -= right.z * dx * panSpeed;
      targetY += dy * panSpeed;
    } else {
      // Orbit
      theta -= dx * 0.01;
      phi = Math.max(0.05, Math.min(Math.PI - 0.05, phi + dy * 0.01));
    }
    update();
  }
  function onUp() { isDragging = false; }
  function onWheel(e) {
    e.preventDefault();
    radius = Math.max(1, Math.min(200, radius * (e.deltaY > 0 ? 1.1 : 0.9)));
    update();
  }
  function onCtx(e) { e.preventDefault(); }

  domElement.addEventListener("mousedown",   onDown);
  window.addEventListener("mousemove",       onMove);
  window.addEventListener("mouseup",         onUp);
  domElement.addEventListener("wheel",       onWheel, { passive: false });
  domElement.addEventListener("contextmenu", onCtx);

  return {
    setTarget: (x, y, z, r) => { targetX=x; targetY=y; targetZ=z; radius=r; update(); },
    update,
    dispose: () => {
      domElement.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
      domElement.removeEventListener("wheel",       onWheel);
      domElement.removeEventListener("contextmenu", onCtx);
    },
  };
}

export default function ThreeDView({ st, onClose }) {
  const containerRef = useRef(null);
  const sceneRef     = useRef(null); // { renderer, scene, camera, controls, animId }
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [showFloors,  setShowFloors]  = useState(true);
  const [showSymbols, setShowSymbols] = useState(true);
  const [wireframe,   setWireframe]   = useState(false);

  // ── Init Three.js scene ────────────────────────────────────────────────────
  const initScene = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      const THREE = await loadThree();

      const W = containerRef.current.clientWidth  || 800;
      const H = containerRef.current.clientHeight || 600;

      // Renderer
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(W, H);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type    = THREE.PCFSoftShadowMap;
      renderer.setClearColor(0x020a14);
      containerRef.current.appendChild(renderer.domElement);

      // Scene
      const scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x020a14, 50, 200);

      // Camera
      const camera = new THREE.PerspectiveCamera(50, W / H, 0.1, 500);

      // Lights
      const ambient = new THREE.AmbientLight(0xffffff, 0.4);
      scene.add(ambient);

      const sun = new THREE.DirectionalLight(0xffffff, 0.8);
      sun.position.set(10, 20, 10);
      sun.castShadow = true;
      sun.shadow.mapSize.width  = 2048;
      sun.shadow.mapSize.height = 2048;
      scene.add(sun);

      const fill = new THREE.DirectionalLight(0x8090b0, 0.3);
      fill.position.set(-10, 5, -10);
      scene.add(fill);

      // Ground grid
      const grid = new THREE.GridHelper(100, 100, 0x0d1520, 0x0d1520);
      scene.add(grid);

      // Build geometry from DCEL
      const { walls, floors, symbols } = buildScene(st, THREE);
      const wallGroup    = new THREE.Group();
      const floorGroup   = new THREE.Group();
      const symbolGroup  = new THREE.Group();
      walls.forEach(m   => wallGroup.add(m));
      floors.forEach(m  => floorGroup.add(m));
      symbols.forEach(m => symbolGroup.add(m));
      scene.add(wallGroup, floorGroup, symbolGroup);

      // Position camera to fit scene
      const bbox = getBoundingBox(st);
      const controls = makeOrbitControls(camera, renderer.domElement);
      controls.setTarget(bbox.cx, 1.5, bbox.cy, bbox.size);

      // Animate
      let animId;
      function animate() {
        animId = requestAnimationFrame(animate);
        renderer.render(scene, camera);
      }
      animate();

      // Resize handler
      const onResize = () => {
        if (!containerRef.current) return;
        const w = containerRef.current.clientWidth;
        const h = containerRef.current.clientHeight;
        renderer.setSize(w, h);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      };
      window.addEventListener("resize", onResize);

      sceneRef.current = {
        renderer, scene, camera, controls, animId,
        wallGroup, floorGroup, symbolGroup,
        onResize, THREE,
      };
      setLoading(false);
    } catch (e) {
      console.error("[ThreeDView]", e);
      setError("WebGL not available or Three.js failed to load.");
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initScene();
    return () => {
      const sc = sceneRef.current;
      if (!sc) return;
      cancelAnimationFrame(sc.animId);
      sc.controls.dispose();
      sc.renderer.dispose();
      window.removeEventListener("resize", sc.onResize);
      if (containerRef.current && sc.renderer.domElement.parentNode === containerRef.current) {
        containerRef.current.removeChild(sc.renderer.domElement);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Rebuild geometry when st changes ──────────────────────────────────────
  useEffect(() => {
    const sc = sceneRef.current;
    if (!sc || loading) return;
    const { scene, wallGroup, floorGroup, symbolGroup, THREE } = sc;

    // Clear old geometry
    [wallGroup, floorGroup, symbolGroup].forEach(g => {
      while (g.children.length) {
        const m = g.children[0];
        m.geometry?.dispose();
        m.material?.dispose();
        g.remove(m);
      }
    });

    // Rebuild
    const { walls, floors, symbols } = buildScene(st, THREE);
    walls.forEach(m   => wallGroup.add(m));
    floors.forEach(m  => floorGroup.add(m));
    symbols.forEach(m => symbolGroup.add(m));
  }, [st, loading]);

  // ── Toggle visibility ──────────────────────────────────────────────────────
  useEffect(() => {
    const sc = sceneRef.current; if (!sc) return;
    sc.floorGroup.visible  = showFloors;
    sc.symbolGroup.visible = showSymbols;
  }, [showFloors, showSymbols]);

  useEffect(() => {
    const sc = sceneRef.current; if (!sc) return;
    sc.wallGroup.traverse(m => {
      if (m.isMesh) m.material.wireframe = wireframe;
    });
  }, [wireframe]);

  // ── UI ─────────────────────────────────────────────────────────────────────
  const MONO = { fontFamily: "'JetBrains Mono','Fira Code',monospace" };
  const btn  = (active, color = "#3b82f6") => ({
    padding: "3px 10px", borderRadius: 3, fontSize: 9, cursor: "pointer",
    background: active ? color + "25" : "transparent",
    border: `1px solid ${active ? color + "60" : "#0d1520"}`,
    color: active ? color : "#2a3a4a",
    ...MONO,
  });

  return (
    <div style={{ position: "absolute", inset: 0, background: "#020a14",
      display: "flex", flexDirection: "column", zIndex: 40 }}>

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
        borderBottom: "1px solid #0d1520", background: "#030508", flexShrink: 0 }}>
        <span style={{ fontSize: 9, color: "#3b82f6", letterSpacing: 1.5, fontWeight: 700, ...MONO }}>
          3D PREVIEW
        </span>
        <div style={{ width: 1, background: "#0d1520", height: 16, margin: "0 4px" }}/>
        <button style={btn(showFloors,  "#4ade80")} onClick={() => setShowFloors(v  => !v)}>Floors</button>
        <button style={btn(showSymbols, "#facc15")} onClick={() => setShowSymbols(v => !v)}>Symbols</button>
        <button style={btn(wireframe,   "#818cf8")} onClick={() => setWireframe(v   => !v)}>Wireframe</button>
        <div style={{ flex: 1 }}/>
        <span style={{ fontSize: 8.5, color: "#1e2a36", ...MONO }}>
          Drag to orbit · Right-drag to pan · Scroll to zoom
        </span>
        <div style={{ width: 1, background: "#0d1520", height: 16, margin: "0 4px" }}/>
        <button onClick={onClose}
          style={{ ...btn(false), color: "#f87171", border: "1px solid #f8717130" }}>
          ✕ Close 3D
        </button>
      </div>

      {/* Canvas container */}
      <div ref={containerRef} style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center", color: "#2a3a4a",
            fontSize: 10, ...MONO }}>
            Building 3D model…
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center", color: "#f87171",
            fontSize: 10, ...MONO }}>
            {error}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, padding: "5px 14px",
        borderTop: "1px solid #0d1520", background: "#030508", flexShrink: 0 }}>
        {[
          ["Walls",   "#8B6358"], ["Floors",  "#1a2030"],
          ["Glass",   "#B0D0FF"], ["Symbols", "#facc1560"],
        ].map(([label, color]) => (
          <span key={label} style={{ display: "flex", alignItems: "center",
            gap: 4, fontSize: 8.5, color: "#2a3a4a", ...MONO }}>
            <span style={{ width: 8, height: 8, borderRadius: 1, background: color, flexShrink: 0 }}/>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
